import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCart, updateCartItem, removeCartItem, clearCart } from "../services/cartService";
import { ProductService, Product } from "../services/productService";
import styles from "../styles/Cart.module.css";

interface CartItem {
  productId: number;
  name: string;
  price: number;
  quantity: number;
  stock_quantity: number;
  images?: {
    id?: number;
    signedUrl: string;
    isPrimary: boolean;
  }[];
}

interface SessionCartItem {
  productId: number;
  quantity: number;
}

// ...imports and interfaces remain the same

const Cart: React.FC = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadCart = async () => {
    setLoading(true);
    try {
      const sessionCart = await getCart();
      const detailedCart: CartItem[] = await Promise.all(
        (sessionCart.cart as SessionCartItem[]).map(async (item) => {
          const product: Product = await ProductService.getProductById(String(item.productId));
          return {
            productId: Number(product.id),
            name: product.name,
            price: Number(product.price),
            quantity: item.quantity,
            stock_quantity: Number(product.stock_quantity),
            images: product.images??[]
          };
        })
      );
      setCart(detailedCart);
    } catch (error) {
      console.error("Failed to load cart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  const handleQuantityChange = async (productId: number, qty: number) => {
    try {
      await updateCartItem(productId, qty);
      await loadCart();
    } catch (error) {
      console.error("Failed to update cart item:", error);
    }
  };

  const handleRemove = async (productId: number) => {
    try {
      await removeCartItem(productId);
      await loadCart();
    } catch (error) {
      console.error("Failed to remove cart item:", error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      setCart([]);
    } catch (error) {
      console.error("Failed to clear cart:", error);
    }
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (loading)
  return (
    <div className="loading">
      <div className="gear" />
      <p>Loading Cart...</p>
    </div>
  );

  return (
    <div className={styles.cartContainer}>
      <h1>Your Cart</h1>
      {cart.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <>
          {cart.map((item) => (
            <div key={item.productId} className={styles.cartItem}>
              <img
                className={styles.itemImage}
                src={
                  item.images?.find((img) => img.isPrimary)?.signedUrl ??
                  item.images?.[0]?.signedUrl ??
                  "/placeholder.png"
                }
                alt={item.name}
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder.png";
                }}
              />
              <div className={styles.itemDetails}>
                <h3>{item.name}</h3>
                <p>UGX {item.price.toLocaleString()}</p>
                  {/* Out of stock message */}
                  {Number(item.stock_quantity) ===0 && (
                    <span className={styles.outOfStockMessage}>
                      Product out of stock to be crafted on order Submission. See our Order processing policy.
                    </span>
                  )}
                <div className={styles.itemControls}>
                <input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(item.productId, Number(e.target.value))}
                />
                  <button
                    className={styles.removeButton}
                    onClick={() => handleRemove(item.productId)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}

          <h3>Total: UGX {total.toLocaleString()}</h3>

          {/* Buttons at bottom row */}
          <div className={styles.cartBottomButtons}>
            <button className={styles.clearCartButton} onClick={handleClearCart}>
              Clear Cart
            </button>
            <button
              className={styles.checkoutButton}
              onClick={() => navigate("/checkout")}
            >
              Proceed to Checkout
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;

